import React from 'react';

import { useNavigate } from 'react-router-dom';
import { CourseStorageService } from '../services/CourseStorageService';
import { Tabs, TabList, Tab, TabPanels, TabPanel, Container, Box, Heading, HStack, Button, useBreakpointValue, Center, Spinner } from '@chakra-ui/react';
import { AppContext } from '../comments/AppContext';
import { SurveyView } from '../course/SurveyView';
import { WorkflowService } from '@/shared/WorkflowService';
import { IWorkflowAction } from '@/shared/IWorkflowAction';
import { ICourseModule } from '@/shared/ICourseModule';
import { CourseModuleStorageService } from '../services/CourseModuleStorageService';
import { CourseStepFileList } from '../course/CourseStepFileList';
import { CourseReviewSidebar } from './CourseReviewSidebar';
import { ButtonRefresh } from '../course/ButtonRefresh';
import { ICourse } from '@/shared/ICourse';
import { AuthService } from '../services/AuthService';
import { ReadOnlyNotice } from '../comments/ReadOnlyNotice';
import { CoursePageMenu } from './CoursePageMenu';
import { WorkflowTextService } from '@/shared/WorkflowTextService';
import { FILENAMES, PLATFORM_VERSION } from '@/shared/constants';
import { AppBreadcrumb } from '../navbar/AppBreadcrumb';

export const CourseReviewPage = (props: {
  course: ICourse,
  setCourse: (course: ICourse | undefined) => void,
  moduleId: string,
  action: string,
  pageId?: string
}) => {

  const { course, setCourse } = props;
  const [modules, setModules] = React.useState<ICourseModule[]>([]);
  const [actions, setActions] = React.useState<IWorkflowAction[]>([]);
  const [action, setAction] = React.useState<string>('view');

  const [module, setModule] = React.useState<ICourseModule | undefined>();

  const { setStatus } = React.useContext(AppContext);

  const navigate = useNavigate();

  const breadcrumb = React.useMemo(() => [
    { href: '/courses', label: 'Courses' },
    { href: `/course/${course.id}`, label: course.folderName },
    { href: `/course/${course.id}/${props.moduleId}/${action}`, label: props.moduleId },
  ], [course, props.moduleId, action]);

  const load = async (moduleId: string, actionId: string, force?: boolean) => {

    const loadedModule = await CourseModuleStorageService.getCourseModule(course.id, moduleId, actionId, force);
    if (actionId === 'new') {
      navigate(`/course/${course.id}/${moduleId}/edit`, { replace: true });
      actionId = 'edit';
    }
    setModule(loadedModule);

    let loadedModules = await CourseModuleStorageService.getCourseModules(course.id, force);
    loadedModules = loadedModules.filter(m => m.id !== 'REVIEW' || (actionId === 'review' || actionId === 'view'));
    setModules(loadedModules);

    if (loadedModules) {
      const roles = AuthService.getCourseRoles(course);
      const loadedActions = WorkflowService.getActions(roles, course, moduleId, loadedModules);
      setActions(loadedActions);

      if (!loadedActions.some(x => x.id === actionId
        || actionId === 'new' && x.id == 'edit'
        || actionId === 'edit' && x.id === 'new'
        || actionId === 'new-small-changes' && x.id === 'edit'
        || actionId === 'new-small-changes' && x.id === 'new'
      )) {
        actionId = 'view'
      }
      setAction(actionId);
      setIsReviewSidebarOpen(actionId !== 'view');
    }
  }

  React.useEffect(() => {
    load(props.moduleId, props.action);
  }, [props.course, props.action]);

  const [tabIndex, setTabIndex] = React.useState(0);

  React.useEffect(() => {

    if (tabIndex > 0) {
      const module = modules[tabIndex - 1];
      setStatus('Loading...');
      CourseModuleStorageService.getCourseModule(course.id, module.id, action).then(loadedModule => {
        const updated = [...modules];
        updated[tabIndex - 1] = loadedModule;
        setModules(updated);
      }).finally(() => {
        setStatus(undefined);
      });
    }
  }, [tabIndex]);

  const [isReviewSidebarOpen, setIsReviewSidebarOpen] = React.useState(false);

  const onRefresh = () => {
    setStatus('Loading...');
    CourseStorageService.getCourse(course.id, true).then(loadedCourse => {
      setCourse(loadedCourse);
    }).finally(() => {
      setStatus(undefined);
    });
  }

  const isExternalExpert = AuthService.isExternalExpert(course);

  const onReviewComplete = async () => {
    navigate(`/course/${course.id}`);
  }

  const [isCommentsPanelOpen, setIsCommentsPanelOpen] = React.useState(false);
  const onCommentsPanelOpen = (isOpen: boolean) => {
    setIsCommentsPanelOpen(isOpen);
  }
  
  const allowPin = !!useBreakpointValue({ base: false, md: true });
  const margin = isReviewSidebarOpen && allowPin ? (isCommentsPanelOpen ? '18em' : '30em') : '0';

  const isReviewAction = action === 'review';

  const pdfFileName = WorkflowTextService.getPdfFileName(course.folderName, props.moduleId, isReviewAction)
  const pdfFileTitle = WorkflowTextService.getPdfFileTitle(props.moduleId, isReviewAction);

  const moduleTemplate = module && (course.coursePlatformVersion === PLATFORM_VERSION.V2
    ? (isReviewAction ? module.workflow_template : module.results_template)
    : (module.results_template)
  );

  const moduleDataProperty = (module && course.coursePlatformVersion === PLATFORM_VERSION.V2 && isReviewAction) ? 'workflow' : 'results'

  const moduleFileName = module && (course.coursePlatformVersion === PLATFORM_VERSION.V2
    ? (isReviewAction ? FILENAMES.WORKFLOW : FILENAMES.RESULTS)
    : FILENAMES.RESULTS
  ) || '';

  return <>
    <AppBreadcrumb breadcrumb={breadcrumb} />
    {module ?
      <Box mt={120} ml={margin} py={{ base: '4', md: '8' }} px={{ base: '0', md: 8 }}>
        <Container>
          <HStack spacing='4' mb="4" justify='space-between'>
            <Heading as="h1" size="sm">{course?.CourseTitle ?? "Loading Course..."}</Heading>
            <HStack>
              {action && action !== 'view' && <Button isDisabled={isReviewSidebarOpen} variant='solid' onClick={() => setIsReviewSidebarOpen(true)}>Provide Feedback</Button>}
              <ButtonRefresh isDisabled={false} onClick={onRefresh} />
              <CoursePageMenu course={course} setCourse={setCourse} isDisabled={false} />
            </HStack>
          </HStack>
        </Container>
        <Tabs isLazy index={tabIndex} onChange={setTabIndex}>
          <Container>
            <TabList>

              <Tab key='0'>Course Files</Tab>
              {modules.map(module => <Tab key={module.id}>{WorkflowTextService.getModuleName(module.id)}</Tab>)}

            </TabList>
          </Container>

          <TabPanels>
            <TabPanel key='0'>
              <Container>
                {modules.map(module => (
                  <CourseStepFileList key={module.id} course={course} module={module} label={WorkflowTextService.getModuleName(module.id)} isExpanded={true} />
                ))}
              </Container>
            </TabPanel>
            {modules.map(module => (
              <TabPanel key={module.id}>
                <SurveyView
                  key={module.id}
                  course={course}
                  module={module}
                  moduleTemplate={module.results_template}
                  moduleDataProperty='results'
                  moduleFileName={''}
                  action='view'
                  onCommentsSidebarOpen={onCommentsPanelOpen}
                  narrowCommentsPanel={isReviewSidebarOpen}
                  enableComments={!isExternalExpert}
                  showMarkup={false}
                />
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
        {action === 'view' && <ReadOnlyNotice course={course} module={module} actions={actions} />}
        {action && action !== 'view' && <CourseReviewSidebar
          label="Course Review"
          allowPin={allowPin}
          size={isCommentsPanelOpen ? 'xs' : 'md'}
          isOpen={isReviewSidebarOpen} onClose={() => setIsReviewSidebarOpen(false)} >
          <SurveyView
            course={course}
            module={module}
            action={action}
            moduleTemplate={moduleTemplate}
            moduleDataProperty={moduleDataProperty}
            moduleFileName={moduleFileName}
            pdfFileName={pdfFileName}
            pdfFileTitle={pdfFileTitle}
            enableComments={false}
            showMarkup={false}
            onComplete={onReviewComplete}
          />
        </CourseReviewSidebar>}
      </Box>
      : <Container mt={200}>
        <Center color='fg.muted' fontSize='large'><Spinner size='lg' /><Box ml={4} >Loading questionnaire...</Box></Center>
      </Container>}
  </>;
}